import { Button, InlineNotification, NumberInput, RadioButton, RadioButtonGroup, TextArea } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { BackLink } from '../components/BackLink';
import { PageHeader } from '../components/PageHeader';
import { asProblem, problemMessage } from '../problems';
import { CustomerFields } from './CustomerFields';
import { DraftLines, ItemPicker } from './ItemPicker';
import { customerOrNull, emptyCustomer, ordersKey, toLineRequest, useOrderSetup, useOrdersApi, type LineDraft } from './model';
import { newKey } from './ordersApi';

/** A new order from the store admin portal, e.g. a phone order (FR-ORD-01). Saved once, even if Create is pressed twice. */
export function CreateOrderPage() {
  const { t } = useTranslation();
  const api = useOrdersApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setup, saleTypeName } = useOrderSetup();
  const [saleTypeId, setSaleTypeId] = useState<string>();
  const [drafts, setDrafts] = useState<LineDraft[]>([]);
  const [customer, setCustomer] = useState(emptyCustomer);
  const [guestCount, setGuestCount] = useState<number | null>(null);
  const [customerNote, setCustomerNote] = useState('');
  const [kitchenNote, setKitchenNote] = useState('');
  // One key for this form: a retry after a lost response returns the order already created (system rule 7).
  const [requestKey] = useState(newKey);
  const saleType = setup?.saleTypes.find((s) => s.id === saleTypeId);

  const create = useMutation({
    mutationFn: ({ saleTypeId: chosen }: { saleTypeId: string }) =>
      api.create({
        saleTypeId: chosen,
        guestCount,
        customer: customerOrNull(customer),
        promisedAt: null,
        customerNote: customerNote || null,
        kitchenNote: kitchenNote || null,
        lines: drafts.map(toLineRequest),
        clientRequestKey: requestKey,
        splitTable: false,
      }),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ordersKey });
      void navigate(`/orders/${order.summary.id}`, { replace: true });
    },
  });

  return (
    <section className="pos-admin-page">
      <BackLink to="/orders">{t('orders.back')}</BackLink>
      <PageHeader title={t('orders.create.title')} />

      <RadioButtonGroup
        legendText={t('orders.create.saleType')}
        name="sale-type"
        valueSelected={saleTypeId}
        onChange={(value) => {
          setSaleTypeId(String(value));
          setDrafts([]); // prices depend on the sale type
        }}
      >
        {(setup?.saleTypes ?? []).map((s) => (
          <RadioButton key={s.id} id={`sale-type-${s.id}`} value={s.id} labelText={saleTypeName(s.id, s.code)} />
        ))}
      </RadioButtonGroup>

      {!saleTypeId ? (
        <p className="pos-admin-page__empty">{t('orders.create.chooseSaleType')}</p>
      ) : (
        <div className="pos-orders__compose">
          <div>
            <h2 className="pos-orders__section-title">{t('orders.create.menu')}</h2>
            <ItemPicker saleTypeId={saleTypeId} onAdd={(draft) => setDrafts((d) => [...d, draft])} />
          </div>
          <div className="pos-orders__compose-side">
            <h2 className="pos-orders__section-title">{t('orders.create.cart')}</h2>
            <DraftLines drafts={drafts} onChange={setDrafts} />
            <p className="pos-orders__muted">{t('orders.create.totalHint')}</p>

            {(saleType?.requiresCustomer || saleType?.requiresAddress || saleType?.baseBehavior !== 'dine_in') && (
              <>
                <h2 className="pos-orders__section-title">{t('orders.details.customer')}</h2>
                <CustomerFields
                  idPrefix="new-order"
                  value={customer}
                  onChange={setCustomer}
                  withAddress={Boolean(saleType?.requiresAddress || saleType?.baseBehavior === 'delivery')}
                />
              </>
            )}
            {saleType?.baseBehavior === 'dine_in' && (
              <NumberInput
                id="new-order-guests"
                label={t('orders.details.guests')}
                min={1}
                max={999}
                allowEmpty
                value={guestCount ?? ''}
                onChange={(_e, { value }) => setGuestCount(value === '' ? null : Number(value))}
              />
            )}
            <TextArea id="new-order-customer-note" labelText={t('orders.details.customerNote')} rows={2} maxCount={1000} value={customerNote} onChange={(e) => setCustomerNote(e.target.value)} />
            <TextArea id="new-order-kitchen-note" labelText={t('orders.details.kitchenNote')} rows={2} maxCount={1000} value={kitchenNote} onChange={(e) => setKitchenNote(e.target.value)} />

            {create.error && <InlineNotification kind="error" lowContrast hideCloseButton title={problemMessage(t, asProblem(create.error))} />}
            <Button disabled={drafts.length === 0 || create.isPending} onClick={() => create.mutate({ saleTypeId })}>
              {t('orders.create.submit')}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}
